import { Card, CardContent, Badge, Button, cn, CLIENT_LOCALE } from '@yeshuman/sdk'
import { Link } from 'react-router-dom'
import { HeartPulse, Pencil, Trash2 } from 'lucide-react'
import type { ConditionItem } from '../hooks/use-conditions'

export const CONDITION_STATUS_LABELS: Record<string, string> = {
  active: 'Active',
  monitoring: 'Monitoring',
  remission: 'Remission',
  resolved: 'Resolved',
}

export function conditionStatusBadgeVariant(status: string) {
  switch (status) {
    case 'active':
      return 'warningSoft' as const
    case 'monitoring':
      return 'defaultSoft' as const
    case 'remission':
    case 'resolved':
      return 'successSoft' as const
    default:
      return 'outline' as const
  }
}

function formatDate(iso?: string | null): string | null {
  if (!iso) return null
  try {
    return new Date(iso).toLocaleDateString(CLIENT_LOCALE, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    })
  } catch {
    return iso
  }
}

interface ConditionCardProps {
  condition: ConditionItem
  onEdit?: (condition: ConditionItem) => void
  onDelete?: (conditionId: string) => void
  isHighlighted?: boolean
  linkToDetail?: boolean
}

export function ConditionCard({
  condition,
  onEdit,
  onDelete,
  isHighlighted,
  linkToDetail = true,
}: ConditionCardProps) {
  const onset = formatDate(condition.onset_date)
  const abatement = formatDate(condition.abatement_date)

  return (
    <Card
      className={cn(
        'transition-all duration-300 hover:shadow-md',
        isHighlighted && 'border-primary ring-1 ring-primary/30',
      )}
    >
      <CardContent className="flex items-start justify-between gap-3 py-4">
        <div className="min-w-0 flex-1 space-y-2">
          <div className="flex flex-wrap items-center gap-2">
            <Badge variant={conditionStatusBadgeVariant(condition.clinical_status)}>
              <HeartPulse className="mr-1 h-3.5 w-3.5" aria-hidden />
              {CONDITION_STATUS_LABELS[condition.clinical_status] ?? condition.clinical_status}
            </Badge>
            <Badge variant="outline">Self-declared</Badge>
          </div>
          <div>
            {linkToDetail ? (
              <Link to={`/conditions/${condition.id}`} className="font-semibold hover:underline">
                {condition.name}
              </Link>
            ) : (
              <h3 className="font-semibold">{condition.name}</h3>
            )}
            <p className="text-sm text-muted-foreground">
              {onset ? `Since ${onset}` : 'Onset not recorded'}
              {abatement ? ` · Resolved ${abatement}` : ''}
            </p>
            {condition.notes ? (
              <p className="mt-1 text-sm text-muted-foreground line-clamp-2">{condition.notes}</p>
            ) : null}
          </div>
        </div>

        {(onEdit || onDelete) && (
          <div className="flex shrink-0 gap-1">
            {onEdit && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0"
                onClick={() => onEdit(condition)}
              >
                <Pencil className="h-4 w-4" />
              </Button>
            )}
            {onDelete && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                onClick={() => onDelete(condition.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  )
}
